import { isUserAuthenticated } from '../others/Configuration';
import getWishlists from '../../api/wishlist/getCacheableWishlists';
import deleteWishlistItem from '../../api/wishlist/deleteWishlistItem';
import translation from '../others/translation';
import fulfill from '../utils/fulfill';
import ellipsis from '../utils/ellipsis';
import WishlistAssignmentRequest from '../WishlistAssignmentRequest.js';

// FIXME use component
// Handle differents styles cases.
const classNames = {
  'thumbnail-btnOpen': 'thumbnail-btnOpen--added', // Desktop: mini FA & thumbnail (search + some other pages)
  'f-toolbarButton': 'f-toolbarButton--added', // Desktop: FA
  'f-faTools__item': 'f-faTools__item--added', // Mobile: FA
};

// Note: prid and catalog can be string or number, it's why we use loose comparison
// Note: return null if no wishlist match
const findWishlistByItem = (wishlists, prid, catalog, offer) =>
  wishlists.find(({ itemsDetails }) =>
    itemsDetails.find(({ Prid, Catalog }) => Prid == prid && Catalog == catalog /* && ??? == offer*/)
  ) || null;
const getWishlistByItem = (prid, catalog, offer, force = false) =>
  fulfill(
    getWishlists(force).then((wishlists) => findWishlistByItem(wishlists, prid, catalog, offer)),
    null
  );

export default class {
  constructor({ el }) {
    this.el = el;
    /**
     * @type {Promise.<null>|null|Promise.<object>|object}
     * @private
     */
    this._wishlist = null;

    this.el.addEventListener('click', this._handleClick.bind(this));
    this.el.ownerDocument.defaultView.addEventListener('wishlistschange', this._handleWishlistUpdate.bind(this));

    this._updateState(isUserAuthenticated ? getWishlistByItem(this.prid, this.catalog, this.offer) : null); // get wishlist (with cache)
  }

  get prid() {
    return this.el.dataset.prid;
  }

  get catalog() {
    return this.el.dataset.catalog;
  }

  get offer() {
    return this.el.dataset.offer;
  }

  async _handleClick(event) {
    // already handled
    if (event.defaultPrevented) {
      return;
    }

    // If a wishlist already exist (wait for it to proceed), remove
    const wishlist = await this._wishlist;
    if (wishlist) {
      if (!(await fulfill(deleteWishlistItem(wishlist.publicId, this.prid, this.catalog, this.offer), false))) {
        // End here, can't remove from wishlist
        return;
      }

      // Lets others know an item has been added to a wishlist
      this.el.dispatchEvent(
        new CustomEvent('wishlistitemremove', {
          bubbles: true,
          detail: {
            prid: this.prid,
            catalog: this.catalog,
            offer: this.offer,
            wishlist: wishlist.publicId,
          },
        })
      );
    } else {
      // Request the user to choose which wishlist to add to
      const request = new WishlistAssignmentRequest({
        prid: this.prid,
        catalog: this.catalog,
        offer: this.offer,
      });
      let wishlistID;
      try {
        wishlistID = await request.show();
      } catch {
        // End here, can't add to wishlist
        return;
      }

      // Lets others know an item has been added to a wishlist
      this.el.dispatchEvent(
        new CustomEvent('wishlistitemadd', {
          bubbles: true,
          detail: {
            prid: this.prid,
            catalog: this.catalog,
            offer: this.offer,
            wishlist: wishlistID,
          },
        })
      );
    }

    const wishlists = await fulfill(getWishlists(true), []); // get wishlists (without cache)
    this._updateState(findWishlistByItem(wishlists, this.prid, this.catalog, this.offer));

    // Lets others know wishlists change
    this.el.ownerDocument.defaultView.dispatchEvent(
      new CustomEvent('wishlistschange', {
        bubbles: true,
        detail: wishlists,
      })
    );
  }

  _handleWishlistUpdate({ detail: wishlists }) {
    this._updateState(findWishlistByItem(wishlists, this.prid, this.catalog, this.offer));
  }

  /**
   *
   * @param {object|null} wishlist
   * @private
   */
  async _updateState(wishlist = null) {
    this._wishlist = wishlist;
    const resolvedWishlist = await wishlist;

    // Check if there is no other pending promise (set before the current one resolve)
    if (this._wishlist !== wishlist) {
      return;
    }

    const [, className] = Object.entries(classNames).find(([key, value]) => this.el.classList.contains(key));
    this.el.classList.toggle(className, resolvedWishlist);

    this.el.title = resolvedWishlist
      ? translation('wishlist.popinheader.button.tooltipadded', ellipsis(resolvedWishlist.name, 20))
      : translation('wishlist.popinheader.button.addtowishlist');

    this.el.setAttribute('aria-label', this.el.title);
  }
}
